import React, { useEffect, useContext, ReactElement } from 'react';
import { Subscription, SubscriptionApiProviderProps as Props, SubscriptionApi } from './constants';
import { SubscriptionsClient } from '../../api/subscriptions/SubscriptionsClient';
import { useCustomerApi } from '../customerApi';
import { signalShopifyCustomerProvidersIsPerksTrue } from '../../../perksInfo/utils';
import { useQueries } from 'react-query';

export const SubscriptionApiProvider = ({ subscriptionTypes, children }: Props): ReactElement<SubscriptionApi> => {
  const { customer, error: customerError, loading: customerLoading, linkedId } = useCustomerApi();
  const customerId = customer?.id;

  const subscriptionQueries = useQueries(
    subscriptionTypes.map(subscriptionType => ({
      queryKey: ['subscriptionApiProvider', subscriptionType, customerId],
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        SubscriptionsClient.getSubscription({ subscriptionType, customerId, signal }),
      enabled: Boolean(customerId),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    }))
  );

  const subscriptions = subscriptionQueries.reduce((acc, query) => {
    if (query?.data?.data?.type) {
      acc[query.data.data.type] = {
        ...query.data.data,
        queryStatus: query.status,
        error: query.error || query.data.error ? query.data.statusCode : undefined,
      };
    }
    return acc;
  }, {} as { [name: string]: Subscription });

  useEffect(() => {
    if (subscriptions?.JBPerks?.isActive) {
      signalShopifyCustomerProvidersIsPerksTrue();
    }
  }, [subscriptions?.JBPerks?.isActive]);

  const refreshSubscriptions = () => {
    subscriptionQueries.forEach(query => query.refetch());
  };

  return (
    <SubscriptionApi.Provider
      value={{
        subscriptions,
        refreshSubscriptions,
        customer,
        customerLoading,
        customerError,
        linkedId,
      }}
    >
      {children}
    </SubscriptionApi.Provider>
  );
};

export const useSubscriptionApi = (): SubscriptionApi => {
  const context = useContext(SubscriptionApi);
  if (context === undefined || context === null) {
    throw new Error('useSubscriptionApi must be used within a SubscriptionApiProvider');
  }
  return context;
};
